import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const brl = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const pct = (v) => `${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const FORMAS_A_VISTA = [
  { value: 'pix', label: 'Pix' },
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'debito', label: 'Débito' },
  { value: 'outra', label: 'Outra' },
];
const BANDEIRAS = [
  { value: 'mastercard', label: 'Mastercard' },
  { value: 'visa', label: 'Visa' },
  { value: 'elo', label: 'Elo' },
  { value: 'amex', label: 'Amex' },
];

/**
 * Botão + Sheet lateral que executa a Análise da Proposta com IA.
 *
 * Props:
 *  - getInitialValues: () => ({ valorOriginal, valorOferecido, custoProdutos,
 *    frete, montagem, comissao, pedidoId, clienteId, valorAvista, parcelas, bandeira, formaAvista })
 *  - label: texto do botão (default "Analisar com IA")
 *  - variant, size, className: repassados ao Button.
 */
export default function AiAnalysisTrigger({
  getInitialValues,
  label = 'Analisar com IA',
  variant = 'outline',
  size = 'sm',
  className = '',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [analise, setAnalise] = useState(null);
  const [loading, setLoading] = useState(false);
  // Estado do formulário — inicializado ao abrir
  const [valorOriginal, setValorOriginal] = useState('');
  const [valorOferecido, setValorOferecido] = useState('');
  const [custoProdutos, setCustoProdutos] = useState('');
  const [frete, setFrete] = useState('');
  const [montagem, setMontagem] = useState('');
  const [comissao, setComissao] = useState('');
  const [outrosCustos, setOutrosCustos] = useState('');
  const [freteGratis, setFreteGratis] = useState(false);
  const [montagemGratis, setMontagemGratis] = useState(false);
  const [valorAvista, setValorAvista] = useState('');
  const [formaAvista, setFormaAvista] = useState('pix');
  const [parcelas, setParcelas] = useState(10);
  const [bandeira, setBandeira] = useState('mastercard');
  const [prazoEntrega, setPrazoEntrega] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [pedidoId, setPedidoId] = useState('');
  const [clienteId, setClienteId] = useState('');

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const abrir = () => {
    const iv = (typeof getInitialValues === 'function' ? getInitialValues() : getInitialValues) || {};
    setValorOriginal(iv.valorOriginal ?? '');
    setValorOferecido(iv.valorOferecido ?? '');
    setCustoProdutos(iv.custoProdutos ?? '');
    setFrete(iv.frete ?? '');
    setMontagem(iv.montagem ?? '');
    setComissao(iv.comissao ?? '');
    setOutrosCustos(iv.outrosCustos ?? '');
    setFreteGratis(!!iv.freteGratis);
    setMontagemGratis(!!iv.montagemGratis);
    setValorAvista(iv.valorAvista ?? '');
    setFormaAvista(iv.formaAvista ?? 'pix');
    setParcelas(iv.parcelas ?? 10);
    setBandeira(iv.bandeira ?? 'mastercard');
    setPrazoEntrega(iv.prazoEntrega ?? '');
    setObservacoes(iv.observacoes ?? '');
    setPedidoId(iv.pedidoId ?? '');
    setClienteId(iv.clienteId ?? '');
    setAnalise(null);
    setOpen(true);
  };

  const classCor = (c) => {
    if (!c) return 'bg-muted';
    if (c === 'MUITO_BOA' || c === 'BOA_ACEITAVEL') return 'bg-green-600 text-white';
    if (c === 'ACEITAVEL_COM_ATENCAO') return 'bg-yellow-500 text-white';
    if (c === 'CONTRAPROPOSTA_RECOMENDADA') return 'bg-orange-500 text-white';
    if (c === 'PRECISA_APROVACAO') return 'bg-blue-600 text-white';
    return 'bg-red-600 text-white';
  };

  const analisar = async () => {
    const vo = parseFloat(valorOriginal) || 0;
    if (vo <= 0) { toast.error('Informe o valor original da venda'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/card-simulator/analyze`, {
        valor_original: vo,
        valor_oferecido: parseFloat(valorOferecido) || vo,
        valor_a_vista: parseFloat(valorAvista) || 0,
        forma_a_vista: formaAvista,
        bandeira,
        parcelas: parseInt(parcelas) || 1,
        custo_produtos: parseFloat(custoProdutos) || 0,
        frete: parseFloat(frete) || 0,
        montagem: parseFloat(montagem) || 0,
        comissao: parseFloat(comissao) || 0,
        outros_custos: parseFloat(outrosCustos) || 0,
        frete_gratis: freteGratis,
        montagem_gratis: montagemGratis,
        prazo_entrega_solicitado: prazoEntrega,
        observacoes_cliente: observacoes,
        pedido_id: pedidoId || undefined,
        cliente_id: clienteId || undefined,
      }, { headers: authHeaders(), timeout: 120000 });
      setAnalise(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro na análise');
    } finally {
      setLoading(false);
    }
  };

  const copiarMensagem = () => {
    const msg = analise?.analise_ia?.mensagem_cliente;
    if (!msg) return;
    navigator.clipboard.writeText(msg);
    toast.success('Mensagem copiada');
  };

  return (
    <>
      <Button type="button" variant={variant} size={size} className={className} disabled={disabled}
              onClick={abrir} data-testid="btn-analisar-ia-pedido">
        <Sparkles className="h-4 w-4 mr-2" />{label}
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />Análise IA da Proposta
            </SheetTitle>
            <SheetDescription>
              Valores puxados automaticamente do pedido. Ajuste custos e forma de pagamento antes de analisar.
            </SheetDescription>
          </SheetHeader>

          <div className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Proposta</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Valor original (R$)</Label>
                  <Input type="number" step="0.01" value={valorOriginal}
                    onChange={(e) => setValorOriginal(e.target.value)} data-testid="ia-ped-valor-original" />
                </div>
                <div>
                  <Label className="text-xs">Valor oferecido pelo cliente (R$)</Label>
                  <Input type="number" step="0.01" value={valorOferecido}
                    onChange={(e) => setValorOferecido(e.target.value)}
                    placeholder="deixe igual ao original se sem contra-proposta"
                    data-testid="ia-ped-valor-oferecido" />
                </div>
                <div>
                  <Label className="text-xs">Valor à vista (R$)</Label>
                  <Input type="number" step="0.01" value={valorAvista}
                    onChange={(e) => setValorAvista(e.target.value)} data-testid="ia-ped-avista" />
                </div>
                <div>
                  <Label className="text-xs">Forma à vista</Label>
                  <Select value={formaAvista} onValueChange={setFormaAvista}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{FORMAS_A_VISTA.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Bandeira do cartão</Label>
                  <Select value={bandeira} onValueChange={setBandeira}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{BANDEIRAS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Parcelas do cartão</Label>
                  <Select value={String(parcelas)} onValueChange={(v) => setParcelas(parseInt(v))}>
                    <SelectTrigger data-testid="ia-ped-parcelas"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 10 }).map((_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}x</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Observações do cliente</Label>
                  <Textarea rows={2} value={observacoes} onChange={(e) => setObservacoes(e.target.value)}
                    placeholder="Ex: Cliente aceita fechar hoje se conseguir mais R$ 200..."
                    data-testid="ia-ped-obs" />
                </div>
                <div>
                  <Label className="text-xs">Prazo entrega solicitado</Label>
                  <Input value={prazoEntrega} onChange={(e) => setPrazoEntrega(e.target.value)} placeholder="ex: 15 dias" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Custos internos (referência: produtos do pedido)</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Custo produtos (R$)</Label>
                  <Input type="number" step="0.01" value={custoProdutos}
                    onChange={(e) => setCustoProdutos(e.target.value)} data-testid="ia-ped-custo" />
                  <div className="text-[10px] text-muted-foreground mt-1">Puxado dos custos unitários × quantidade dos produtos do pedido.</div>
                </div>
                <div>
                  <Label className="text-xs">Frete (R$)</Label>
                  <Input type="number" step="0.01" value={frete} onChange={(e) => setFrete(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Montagem (R$)</Label>
                  <Input type="number" step="0.01" value={montagem} onChange={(e) => setMontagem(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Comissão (R$)</Label>
                  <Input type="number" step="0.01" value={comissao} onChange={(e) => setComissao(e.target.value)} />
                </div>
                <div>
                  <Label className="text-xs">Outros custos (R$)</Label>
                  <Input type="number" step="0.01" value={outrosCustos} onChange={(e) => setOutrosCustos(e.target.value)} />
                </div>
                <div className="flex flex-col gap-1 justify-center">
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={freteGratis} onChange={(e) => setFreteGratis(e.target.checked)} /> Frete grátis solicitado
                  </label>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={montagemGratis} onChange={(e) => setMontagemGratis(e.target.checked)} /> Montagem grátis solicitada
                  </label>
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-end">
              <Button onClick={analisar} disabled={loading} size="lg" data-testid="ia-ped-btn-analisar">
                <Sparkles className="h-4 w-4 mr-2" />{loading ? 'Analisando com IA...' : 'Analisar proposta com IA'}
              </Button>
            </div>

            {analise && (
              <div className="space-y-4" data-testid="ia-ped-result-panel">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={classCor(analise.analise_ia?.classificacao) + ' text-sm'} data-testid="ia-ped-classificacao">
                    {(analise.analise_ia?.classificacao || '').replace(/_/g, ' ')}
                  </Badge>
                  <Badge variant="outline">Decisão: {analise.analise_ia?.decisao_sugerida}</Badge>
                  <Badge variant="secondary">Confiança: {analise.analise_ia?.nivel_confianca}</Badge>
                  {analise.analise_ia?._fallback && <Badge variant="destructive">Fallback (IA indisponível)</Badge>}
                </div>

                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Resumo</CardTitle></CardHeader>
                  <CardContent className="text-sm">{analise.analise_ia?.resumo}</CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm">Números do sistema</CardTitle></CardHeader>
                  <CardContent className="grid grid-cols-2 gap-2 text-xs">
                    <div><span className="text-muted-foreground">Valor final:</span> <strong>{brl(analise.calculos.valor_final_venda)}</strong></div>
                    <div><span className="text-muted-foreground">Desconto gerado:</span> <strong>{brl(analise.calculos.desconto_gerado_pelo_pagamento_a_vista)}</strong></div>
                    <div><span className="text-muted-foreground">Cartão original:</span> {brl(analise.calculos.valor_original_cartao)}</div>
                    <div><span className="text-muted-foreground">Cartão final:</span> <strong>{brl(analise.calculos.valor_final_cartao)}</strong></div>
                    <div><span className="text-muted-foreground">Taxa efetiva:</span> {brl(analise.calculos.taxa_efetiva_cartao)}</div>
                    <div><span className="text-muted-foreground">Cliente paga:</span> <strong>{brl(analise.calculos.total_pago_cliente)}</strong></div>
                    <div><span className="text-muted-foreground">Líquido total:</span> <strong className="text-green-700">{brl(analise.calculos.valor_liquido_total)}</strong></div>
                    <div><span className="text-muted-foreground">Lucro estimado:</span> <strong>{brl(analise.calculos.lucro_estimado)}</strong></div>
                    <div><span className="text-muted-foreground">Margem:</span> <strong>{pct(analise.calculos.margem_percentual)}</strong></div>
                  </CardContent>
                </Card>

                {analise.analise_ia?.pontos_positivos?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-green-700">✓ Pontos positivos</CardTitle></CardHeader>
                    <CardContent><ul className="text-sm space-y-1 list-disc list-inside">
                      {analise.analise_ia.pontos_positivos.map((p, i) => <li key={i}>{p}</li>)}
                    </ul></CardContent>
                  </Card>
                )}

                {analise.analise_ia?.pontos_atencao?.length > 0 && (
                  <Card className="border-yellow-500/40">
                    <CardHeader className="pb-2"><CardTitle className="text-sm text-yellow-700">⚠ Pontos de atenção</CardTitle></CardHeader>
                    <CardContent><ul className="text-sm space-y-1 list-disc list-inside">
                      {analise.analise_ia.pontos_atencao.map((p, i) => <li key={i}>{p}</li>)}
                    </ul></CardContent>
                  </Card>
                )}

                {analise.analise_ia?.contrapropostas?.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Contrapropostas sugeridas</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                      {analise.analise_ia.contrapropostas.map((cp, i) => (
                        <div key={i} className="border rounded p-3 text-sm space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="font-semibold">{cp.titulo}</span>
                            <Badge variant="outline" className="text-xs">{cp.tipo?.replace(/_/g, ' ')}</Badge>
                          </div>
                          {cp.descricao && <div>{cp.descricao}</div>}
                          {cp.justificativa && <div className="text-xs text-muted-foreground italic">{cp.justificativa}</div>}
                          {cp.exige_aprovacao && <Badge variant="destructive" className="text-xs">Exige aprovação do administrador</Badge>}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {analise.analise_ia?.mensagem_cliente && (
                  <Card className="border-primary/40">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center justify-between">
                        <span>Mensagem para o cliente (WhatsApp)</span>
                        <Button size="sm" variant="outline" onClick={copiarMensagem} data-testid="ia-ped-btn-copiar-msg">Copiar</Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm whitespace-pre-wrap bg-muted/50 rounded p-3">
                        {analise.analise_ia.mensagem_cliente}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
